import * as React from "react"
import { Button, Icon, cn } from "@nexus/ui"
import { useTranslation } from "react-i18next"
import {
  DndContext,
  closestCenter,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from "@dnd-kit/core"
import {
  SortableContext,
  rectSortingStrategy,
  useSortable,
  arrayMove,
} from "@dnd-kit/sortable"
import { CSS } from "@dnd-kit/utilities"
import type { RowSelectionState } from "@tanstack/react-table"
import type { Opportunity } from "../../../types/opportunities.types"
import {
  readStoredOpportunityRowOrder,
  sortByStoredOpportunityIds,
  writeStoredOpportunityRowOrder,
} from "../../../lib/opportunity-row-order"
import { OPPORTUNITY_DRAG_REORDER_GRID_CLASS } from "../../../lib/opportunity-drag-reorder-grid-classes"
import {
  OpportunityRowDragContext,
  OpportunityRowDragHandle,
} from "../../opportunity-row/OpportunityRowDrag"
import { OpportunityCard, OpportunityCardSkeleton } from "../OpportunityCard/OpportunityCard"

function SortableOpportunityCardItem({
  id,
  mountDelayIndex,
  children,
}: {
  id: string
  mountDelayIndex: number
  children: React.ReactNode
}) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id })

  const dragCtx = React.useMemo(() => ({ listeners, attributes }), [listeners, attributes])

  return (
    <div
      ref={setNodeRef}
      style={{
        transform: CSS.Transform.toString(transform),
        transition,
        zIndex: isDragging ? 10 : undefined,
      }}
      className={cn(
        "group/row relative min-w-0 overflow-x-visible",
        isDragging && "opacity-40",
      )}
    >
      <OpportunityRowDragContext value={dragCtx}>
        <OpportunityRowDragHandle variant="card" />
        <div
          style={{ animationDelay: `${Math.min(mountDelayIndex, 18) * 30}ms` }}
          className="motion-safe:animate-in motion-safe:fade-in motion-safe:zoom-in-95 motion-safe:slide-in-from-bottom-2 motion-safe:fill-mode-both motion-safe:duration-300 motion-safe:ease-out motion-reduce:animate-none"
        >
          {children}
        </div>
      </OpportunityRowDragContext>
    </div>
  )
}

export interface OpportunityCardGridProps {
  tenantId: string
  setId: string
  data?: Opportunity[]
  rowSelection: RowSelectionState
  onRowSelectionChange: (state: RowSelectionState) => void
  onRowClick: (opportunity: Opportunity) => void
  hasActiveFilters?: boolean
  onClearFilters?: () => void
  isFetchingNextPage?: boolean
  search?: string
  onManualRowReorder?: () => void
  /** When true, show API order and ignore stored drag ids. */
  columnSortActive?: boolean
}

export function OpportunityCardGrid({
  tenantId,
  setId,
  data,
  rowSelection,
  onRowSelectionChange,
  onRowClick,
  hasActiveFilters,
  onClearFilters,
  isFetchingNextPage,
  search = "",
  onManualRowReorder,
  columnSortActive = false,
}: OpportunityCardGridProps) {
  const { t } = useTranslation()

  const handleSelect = React.useCallback(
    (id: string, selected: boolean) => {
      const next = { ...rowSelection }
      if (selected) {
        next[id] = true
      } else {
        delete next[id]
      }
      onRowSelectionChange(next)
    },
    [rowSelection, onRowSelectionChange],
  )

  const [rowOrder, setRowOrder] = React.useState<string[]>(() =>
    readStoredOpportunityRowOrder(tenantId, setId),
  )

  React.useEffect(() => {
    setRowOrder(readStoredOpportunityRowOrder(tenantId, setId))
  }, [tenantId, setId])

  const sortedData = React.useMemo(
    () =>
      columnSortActive
        ? (data ?? [])
        : sortByStoredOpportunityIds(data ?? [], rowOrder),
    [columnSortActive, data, rowOrder],
  )

  const rowDndSensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
  )

  const handleRowDragEnd = React.useCallback((event: DragEndEvent) => {
    const { active, over } = event
    if (!over || active.id === over.id) return
    const ids = sortedData.map((o) => o.id)
    const oldIndex = ids.indexOf(active.id as string)
    const newIndex = ids.indexOf(over.id as string)
    if (oldIndex === -1 || newIndex === -1) return
    const reordered = arrayMove(ids, oldIndex, newIndex)
    setRowOrder(reordered)
    writeStoredOpportunityRowOrder(tenantId, setId, reordered)
    onManualRowReorder?.()
  }, [sortedData, tenantId, setId, onManualRowReorder])

  if (!data || data.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center gap-3 py-16 text-center">
        <Icon name="lightbulb" size="lg" className="text-muted-foreground/30" />
        <p className="text-sm text-muted-foreground">
          {hasActiveFilters
            ? t("opportunities.table.emptyFilters")
            : t("opportunities.table.emptyTab")}
        </p>
        {hasActiveFilters && onClearFilters && (
          <Button variant="outline" size="sm" onClick={onClearFilters}>
            {t("common.clearFilters", "Clear filters")}
          </Button>
        )}
      </div>
    )
  }

  return (
    <div className="min-w-0 overflow-x-visible pb-4">
      <DndContext sensors={rowDndSensors} collisionDetection={closestCenter} onDragEnd={handleRowDragEnd}>
        <SortableContext items={sortedData.map((o) => o.id)} strategy={rectSortingStrategy}>
          <div className={OPPORTUNITY_DRAG_REORDER_GRID_CLASS}>
            {sortedData.map((opportunity, index) => (
              <SortableOpportunityCardItem
                key={opportunity.id}
                id={opportunity.id}
                mountDelayIndex={index}
              >
                <OpportunityCard
                  opportunity={opportunity}
                  isSelected={!!rowSelection[opportunity.id]}
                  onSelect={handleSelect}
                  onClick={onRowClick}
                  search={search}
                  mountDelay={Math.min(index, 18) * 30 + 300}
                />
              </SortableOpportunityCardItem>
            ))}
            {isFetchingNextPage &&
              [1, 2, 3].map((i) => <OpportunityCardSkeleton key={`skeleton-${i}`} />)}
          </div>
        </SortableContext>
      </DndContext>
    </div>
  )
}
